import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { UsersFilterBarProps } from '../../../types/dashboardV2';
import { FilterBar as GenericFilterBar } from '../../../components/common/FilterBar/FilterBar';
import { StatusFilterDropdown } from '../../../components/common/StatusFilterDropdown/StatusFilterDropdown';
import { DateRangePicker } from '../../../components/common/DateRangePicker/DateRangePicker';
import { FilterToggle } from '../../../components/common/FilterToggle/FilterToggle';
import {
  USER_ROLE_OPTIONS,
  USER_STATUS_OPTIONS,
} from '../../../constants/filterOptions';
import { UserRole, UserStatus } from '../../../shared/enums/status';

export const UsersFilterBar = memo(function UsersFilterBar({
  filters,
  onFilterChange,
  onRefresh,
}: UsersFilterBarProps) {
  const { t } = useTranslation();

  const handleRoleChange = useCallback(
    (role: UserRole | '') => onFilterChange({ role }),
    [onFilterChange],
  );

  const handleStatusChange = useCallback(
    (status: UserStatus | '') => onFilterChange({ status }),
    [onFilterChange],
  );

  const handleDateRangeChange = useCallback(
    (dateRange: string) => onFilterChange({ dateRange: dateRange === 'Custom Range' ? '' : dateRange }),
    [onFilterChange],
  );

  const handleSearchChange = useCallback(
    (search: string) => onFilterChange({ search }),
    [onFilterChange],
  );

  const handleActiveSessionToggle = useCallback(
    (hasActiveSession: boolean) => onFilterChange({ hasActiveSession }),
    [onFilterChange],
  );

  const handleHasTelegramToggle = useCallback(
    (hasTelegram: boolean) => onFilterChange({ hasTelegram }),
    [onFilterChange],
  );

  return (
    <GenericFilterBar
      search={{
        value: filters.search,
        onChange: handleSearchChange,
        placeholder: t('dashboard.users.searchPlaceholder'),
        ariaLabel: t('dashboard.users.searchPlaceholder'),
        name: 'user-search',
        type: 'text',
      }}
      controls={
        <>
          <StatusFilterDropdown
            value={filters.role}
            options={USER_ROLE_OPTIONS}
            onChange={handleRoleChange}
            ariaLabel={t('dashboard.filters.aria.filterUsersRole')}
            iconName="badge"
          />
          <StatusFilterDropdown
            value={filters.status}
            options={USER_STATUS_OPTIONS}
            onChange={handleStatusChange}
            ariaLabel={t('dashboard.filters.aria.filterUsersStatus')}
          />
          <DateRangePicker
            value={filters.dateRange || 'Last 30 days'}
            onChange={handleDateRangeChange}
          />
          <FilterToggle
            label={t('dashboard.filters.toggles.activeSession')}
            value={filters.hasActiveSession}
            onChange={handleActiveSessionToggle}
            icon="bolt"
          />
          <FilterToggle
            label={t('dashboard.filters.toggles.telegram')}
            value={filters.hasTelegram}
            onChange={handleHasTelegramToggle}
            icon="send"
          />
        </>
      }
      privacyScope="users"
      onRefresh={onRefresh}
      refreshAriaLabel={t('dashboard.filters.aria.refreshUsers')}
    />
  );
});

export default UsersFilterBar;
